<!--
  DonutChart — where the month's spending went, at a glance.

  The hole is not wasted space: it holds the one number the owner actually
  wants first (the period's total), so the chart answers "how much" before it
  answers "on what". Everything else is a `Slice` the caller already priced
  and formatted — this component only turns values into angles (via
  `donutArcs`, src/lib/charts/arc.ts) and angles into a `<path>`.

  A slice is a toggle, not a link: click it (or focus it and press Enter/
  Space) and `onselect(key)` fires so the ledger below can filter to that
  category; click the same slice again and `onselect(null)` clears it. The
  selected slice nudges outward along its own `midAngle` — `donutArcs` hands
  that angle back per arc, so the offset is read off the geometry already
  computed rather than re-derived.

  Colour is assigned by a slice's position in the `slices` prop *as given*,
  not its position after zero/negative values are dropped — see the ramp
  comment in admin-look.css. That keeps a category's colour identical here
  and in BarChart/ChartLegend even when this donut quietly skips a category
  that has nothing in it this period.

  `role="img"` is deliberately left off the <svg>: that role would flatten
  its subtree in the accessibility tree, hiding the very buttons this chart
  depends on. `aria-labelledby` on an internal <title> gives it a name
  instead, and the slices are exposed as ordinary focusable buttons. The
  visually-hidden <table> is the tab-order-free alternative for anyone who
  would rather read the numbers than hunt for them in a ring of buttons.
-->
<script lang="ts">
  import { donutArcs } from '../../../lib/charts/arc';

  interface Slice {
    key: string;
    label: string;
    value: number;
    /** Already formatted by the caller — this component never rounds. */
    display: string;
  }

  interface Props {
    slices: readonly Slice[];
    /** The figure that sits in the hole, already formatted. */
    total: string;
    /** What that figure is, e.g. "spent in August". */
    caption: string;
    selected?: string | null;
    onselect?: (key: string | null) => void;
  }

  const { slices, total, caption, selected = null, onselect }: Props = $props();

  const RAMP_SIZE = 8;
  const OUTER = 100;
  const INNER = 62;
  /** How far a selected slice steps outward, in the same user units as the arc paths. */
  const LIFT = 6;

  // A stable id per instance: several breakdown charts can be on one screen.
  const titleId = `donut-title-${Math.random().toString(36).slice(2, 9)}`;

  /**
   * Slices with something to draw, still carrying the index they had in the
   * caller's original list — that index, not the position in this filtered
   * array, is what picks the ramp colour (see the file comment above).
   */
  const positive = $derived(
    slices
      .map((slice, index) => ({ slice, colorIndex: index % RAMP_SIZE }))
      .filter((s) => Number.isFinite(s.slice.value) && s.slice.value > 0)
  );

  // donutArcs applies the exact same finite-and-positive filter internally,
  // so feeding it the already-filtered values keeps `arcs[i]` lined up with
  // `positive[i]` one-to-one with nothing dropped a second time.
  const arcs = $derived(donutArcs(positive.map((p) => p.slice.value), OUTER, INNER));

  const sum = $derived(positive.reduce((total, p) => total + p.slice.value, 0));

  /**
   * A whole-number share for the hidden table only. This is the one place a
   * chart component computes anything beyond the caller's `display` string —
   * it is not a money figure and never appears as the headline number, so it
   * cannot produce the "$1,234.5 vs $1,234.50" mismatch the no-formatting
   * rule exists to prevent.
   */
  function share(value: number): string {
    if (!(sum > 0)) return '—';
    return `${Math.round((value / sum) * 100)}%`;
  }

  function toggle(key: string) {
    onselect?.(selected === key ? null : key);
  }

  function onKeydown(event: KeyboardEvent, key: string) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggle(key);
    }
  }

  function lift(midAngle: number, isSelected: boolean): string {
    if (!isSelected) return '';
    const dx = Math.cos(midAngle) * LIFT;
    const dy = Math.sin(midAngle) * LIFT;
    return `translate(${dx.toFixed(2)}px, ${dy.toFixed(2)}px)`;
  }
</script>

{#if positive.length === 0}
  <div class="donut-empty">Nothing spent in this period.</div>
{:else}
  <div class="donut">
    <svg viewBox="0 0 240 240" aria-labelledby={titleId}>
      <title id={titleId}>{caption}: {total}</title>
      <g transform="translate(120, 120)">
        {#each positive as p, i (p.slice.key)}
          {@const arc = arcs[i]}
          {#if arc}
            {@const isSelected = selected === p.slice.key}
            <path
              class="slice"
              class:slice--selected={isSelected}
              class:slice--dimmed={selected !== null && !isSelected}
              d={arc.d}
              fill="var(--chart-{p.colorIndex + 1})"
              role="button"
              tabindex="0"
              aria-pressed={isSelected}
              aria-label="{p.slice.label}, {p.slice.display}"
              style:transform={lift(arc.midAngle, isSelected)}
              onclick={() => toggle(p.slice.key)}
              onkeydown={(e) => onKeydown(e, p.slice.key)}
            ></path>
          {/if}
        {/each}
        <text x="0" y="-4" text-anchor="middle" class="donut__total">{total}</text>
        <text x="0" y="16" text-anchor="middle" class="donut__caption">{caption}</text>
      </g>
    </svg>

    <table class="visually-hidden">
      <caption>{caption}: {total}</caption>
      <thead>
        <tr>
          <th scope="col">Category</th>
          <th scope="col">Amount</th>
          <th scope="col">Share</th>
        </tr>
      </thead>
      <tbody>
        {#each positive as p (p.slice.key)}
          <tr>
            <th scope="row">{p.slice.label}</th>
            <td>{p.slice.display}</td>
            <td>{share(p.slice.value)}</td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{/if}

<style>
  .donut {
    display: grid;
    justify-items: center;
  }

  svg {
    width: 100%;
    max-width: 15rem;
    height: auto;
  }

  .slice {
    cursor: pointer;
    stroke: var(--paper);
    stroke-width: 1.5;
    transition: transform 160ms ease-out, opacity 160ms ease-out, filter 160ms ease-out;
    touch-action: manipulation;
  }
  .slice:hover { filter: brightness(1.05); }
  .slice:focus-visible {
    outline: 3px solid var(--brand);
    outline-offset: 2px;
  }
  .slice--selected {
    stroke: var(--brand-dark);
    stroke-width: 2;
  }
  .slice--dimmed {
    opacity: 0.45;
  }

  .donut__total {
    font-size: 1.7rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    fill: var(--ink);
    font-variant-numeric: tabular-nums;
  }
  .donut__caption {
    font-size: 0.62rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    fill: var(--ink-faint);
  }

  .donut-empty {
    display: grid;
    place-items: center;
    min-height: 12rem;
    padding: 1.2rem;
    text-align: center;
    color: var(--ink-soft);
    background: var(--bg-sunken);
    border-radius: var(--radius-lg);
  }

  @media (prefers-reduced-motion: reduce) {
    .slice { transition: opacity 160ms ease-out; }
  }
</style>
